"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ATELIER_THEME = void 0;
const common_1 = require("./common");
exports.ATELIER_THEME = (0, common_1.firstPartyTheme)({
    id: 'atelier',
    name: 'Atelier',
    tagline: 'Editorial luxury: bone paper, ink type and one brass accent, set like a fashion quarterly.',
    description: 'Atelier dresses a brand like a printed luxury quarterly. A warm bone ground, near-black ink, a single antique-brass accent and a high-contrast Cormorant Garamond display set against a quiet Work Sans text face. Generous margins, hairline rules and photography given room to breathe. Built for fashion and jewellery houses, boutique hotels, fragrance, interiors and any brand that sells on restraint and craft.',
    categories: ['luxury', 'editorial'],
    tokens: {
        colors: {
            background: '#f6f2ec',
            surface: '#ede6dc',
            text: '#1c1917',
            muted: '#6b635a',
            primary: '#1c1917',
            primaryText: '#f6f2ec',
            accent: '#9a7b4f',
            border: '#d9cfc2',
        },
        darkColors: {
            background: '#14110f',
            surface: '#1e1a17',
            text: '#efe8de',
            muted: '#a39a8e',
            primary: '#efe8de',
            primaryText: '#14110f',
            accent: '#c4a170',
            border: '#2f2924',
        },
        fonts: { heading: 'Cormorant Garamond', body: 'Work Sans' },
        radius: 0,
        density: 'airy',
        typeScale: 1.5,
    },
    guide: `## Mood
A printed quarterly you find in a hotel suite: unhurried, confident, a little aloof. Luxury here is restraint. The page is mostly paper; the product and the type do the talking. Nothing shouts, nothing bounces, nothing is free.

## Grid and layout
- Think in a 12-column grid with wide outer margins (at least 6vw on desktop). Let text columns sit off-centre: a 5-column text block starting at column 2 or 7, never a centred paragraph wall.
- Asymmetry is the habit. Pair a tall portrait image (4:5 or 2:3) with a short caption block that aligns to its baseline, not its top.
- Separate sections with whitespace and a single hairline rule (1px, border colour), never with background stripes or cards.
- Use section numbering in small caps ("I. The Collection", "II. Maison") set in the text face, tracked out (letter-spacing 0.18em), muted colour.

## Typography
- Display headlines in Cormorant Garamond at the large fluid steps (4xl and 5xl), weight 500, tight leading. Use the italic for one word per headline at most, the word that carries the feeling.
- Body copy in Work Sans at 15–17px, weight 400, line-height 1.7, max 62 characters per line.
- Labels, prices and navigation: Work Sans, uppercase, 11–12px, tracked 0.16em.
- Never bold the serif. Never set body copy in the serif.

## Colour
Bone and ink carry 95% of every screen. Brass is a signature, not a fill: a thin underline under the active nav item, a price, a numeral, an ornament rule. Buttons are solid ink with bone text, square corners, generous padding (16px 32px). Secondary actions are text links with a 1px underline offset 6px.

## Imagery
Natural light, deep shadows, real texture (linen, stone, skin, metal). Prefer still-life and detail crops over wide lifestyle shots. Warm, slightly desaturated grade; no pure white backgrounds, no cut-out product on white. Full-bleed only once per page, for the hero or a single statement image.

## Motion
Slow and weighted. Fades and 8–12px rises over 700–900ms with a soft ease-out (cubic-bezier(0.22, 1, 0.36, 1)). Images reveal with a gentle clip from bottom to top. No parallax gimmicks, no spring bounces, no hover scaling beyond 1.02.

## Copy tone
Short, declarative, sensory. Third person for the house ("The atelier works in small runs"), second person sparingly. No exclamation marks, no "shop now", no discount language above the fold. Prefer "Discover the collection", "Book a private appointment", "Read the story".

## Do
- Leave at least one screen-height of near-empty paper somewhere on the home page.
- Use real craft details: materials, origin, hours of handwork, edition sizes.
- Keep the nav to four items plus the wordmark.

## Don't
- No gradients, drop shadows, rounded pills, emoji or icon grids.
- No more than one accent element per viewport.
- Never stack three centred sections in a row.`,
    sections: [
        {
            name: 'Hero: cover story',
            recipe: 'Full-viewport section. Left 5 columns: section label in tracked small caps, then a two-line Cormorant headline at the 5xl step with one italic word, then a single sentence of body copy and one ink button. Right 7 columns: a single 4:5 portrait image bleeding off the right and top edges. A brass hairline (40px wide) sits above the label. On mobile the image comes first at 80vh, the text block below with 24px gutters.',
        },
        {
            name: 'Collection index',
            recipe: 'A numbered list, not a card grid. Each row: roman numeral (muted, text face), collection name in the serif at the 2xl step, a short descriptor, and a thumbnail that appears to the right on hover (desktop) or inline (mobile). Rows separated by 1px border-colour rules, 32px vertical padding. The hovered row name shifts 8px right over 400ms.',
        },
        {
            name: 'Product detail',
            recipe: 'Two columns. Left: a vertical stack of large images (no carousel), each full column width. Right: a sticky column with product name in the serif (3xl), price in tracked uppercase with the brass colour, a short paragraph, materials as a definition list, then the ink add-to-bag button full width. Below: "The making" in an off-centre 5-column text block.',
        },
        {
            name: 'Maison story',
            recipe: 'A long-form editorial section: large serif pull quote (4xl, italic, centred within a 8-column measure) followed by two offset text columns with a drop cap in the serif, and a single landscape image between them. Caption under the image in 12px uppercase tracked muted text.',
        },
    ],
});
